import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, FolderKanban, Loader2, Package, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useCreateBudget } from "../hooks/useBudgets";

type LockedFields = {
  companyId?: boolean;
  productId?: boolean;
  projectId?: boolean;
};

type PrefillContext = {
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  locked?: LockedFields;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  prefill?: PrefillContext;
};

export function NewBudgetDrawer({
  isOpen,
  onClose,
  onCreated,
  prefill,
}: Props) {
  const createBudget = useCreateBudget();
  const { data: typeItems = [] } = useCategoryItems("budget_type");
  const { data: currencyItems = [] } = useCategoryItems("currency");

  const lockCompany = !!prefill?.locked?.companyId && !!prefill?.companyId;
  const lockProduct = !!prefill?.locked?.productId && !!prefill?.productId;
  const lockProject = !!prefill?.locked?.projectId && !!prefill?.projectId;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("project");
  const [totalAmount, setTotalAmount] = useState("");
  const [currency, setCurrency] = useState("AED");
  const [fiscalYear, setFiscalYear] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    prefill?.companyId ?? "",
  );
  const [selectedProductId, setSelectedProductId] = useState(
    prefill?.productId ?? "",
  );
  const [selectedProjectId, setSelectedProjectId] = useState(
    prefill?.projectId ?? "",
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId(prefill?.companyId ?? "");
      setSelectedProductId(prefill?.productId ?? "");
      setSelectedProjectId(prefill?.projectId ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prefill?.companyId, prefill?.productId, prefill?.projectId]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  function reset() {
    setName("");
    setDescription("");
    setType("project");
    setTotalAmount("");
    setCurrency("AED");
    setFiscalYear("");
    setStartDate("");
    setEndDate("");
    setSelectedCompanyId(prefill?.companyId ?? "");
    setSelectedProductId(prefill?.productId ?? "");
    setSelectedProjectId(prefill?.projectId ?? "");
    setSelectedPhaseId(null);
    setSelectedStageId(null);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const amt = Number(totalAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError("Total amount must be a positive number");
      return;
    }
    setError(null);
    try {
      const res = await createBudget.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        type,
        totalAmount: amt,
        currency,
        fiscalYear: fiscalYear ? Number(fiscalYear) : null,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        companyId: selectedCompanyId || null,
        productId: selectedProductId || null,
        projectId: selectedProjectId || null,
        phaseId: selectedPhaseId,
        stageId: selectedStageId,
      });
      onCreated?.(res.budget.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create budget");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New Budget</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Budget Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="bdg-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="bdg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q2 Marketing Budget"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bdg-type">Type</Label>
              <Select
                id="bdg-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {typeItems.length === 0 ? (
                  <option value="project">Project</option>
                ) : (
                  typeItems.map((t) => (
                    <option key={t.id} value={t.value}>
                      {t.label}
                    </option>
                  ))
                )}
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bdg-desc">Description</Label>
              <textarea
                id="bdg-desc"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the budget (optional)"
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">Amount</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bdg-total">
                  Total Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="bdg-total"
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bdg-currency">Currency</Label>
                <Select
                  id="bdg-currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {currencyItems.length === 0 ? (
                    <option value="AED">AED</option>
                  ) : (
                    currencyItems.map((c) => (
                      <option key={c.id} value={c.value.toUpperCase()}>
                        {c.label}
                      </option>
                    ))
                  )}
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bdg-fy">Fiscal Year</Label>
              <Input
                id="bdg-fy"
                type="number"
                placeholder="e.g. 2026"
                value={fiscalYear}
                onChange={(e) => setFiscalYear(e.target.value)}
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">Period</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bdg-start">Start Date</Label>
                <Input
                  id="bdg-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bdg-end">End Date</Label>
                <Input
                  id="bdg-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Association</h3>

            <div className="space-y-2">
              <Label htmlFor="bdg-company">Company</Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="bdg-company"
                  value={selectedCompanyId}
                  disabled={lockCompany}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedProductId("");
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bdg-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="bdg-product"
                  value={selectedProductId}
                  disabled={lockProduct}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bdg-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="bdg-project"
                  value={selectedProjectId}
                  disabled={lockProject}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    setSelectedPhaseId(null);
                    setSelectedStageId(null);
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedProjectId && (
              <PhaseStagePicker
                projectId={selectedProjectId || null}
                phaseId={selectedPhaseId}
                stageId={selectedStageId}
                onPhaseChange={setSelectedPhaseId}
                onStageChange={setSelectedStageId}
              />
            )}

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createBudget.isPending}
            >
              {createBudget.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Budget
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
